import { ClientActionService } from "@cervval.js/client-action/src/services/client.action.service";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { CustomElement } from "@cervval.js/client-html5-spa/navigation";
import { ButtonSize, CjsButton } from "@cervval.js/client-html5-ui/src/components/button/button";
import { CjsDropdownButton, DropdownButtonItemClickEvent } from "@cervval.js/client-html5-ui/src/components/dropdownButton/dropdownButton";
import { CatchErrorWithDialog } from "@cervval.js/client-html5-ui/src/tools/catchErrorWithDialog.decorator";
import { ACTION_SERVICE } from "@cervval.js/common-action/src/services/action.service";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { NOW } from "@cervval.js/common/src/tools/timestamps.tools.factory";
import { add, format, toDate } from "@cervval.js/common/src/tools/timestamps.tools.tsdate";
import { BookingSession, BookingUserSession } from "@ehealth_tools/common-booking/src/data/booking.dto";
import { BOOKING_SERVICE } from "@ehealth_tools/common-booking/src/services/abstractCommonBooking.service";
import { formatInitials } from "@ehealth_tools/common/src/data/user/user.tools";
import { AbstractClientBookingService } from "../../services/abstractClientBooking.service";
import { _I18nAppointmentCard } from "./appointmentCard.i18n";
import { anyReallyAny } from "@cervval.js/common/tools";
export interface AppointmentCardOptions {
    userSession?: BookingUserSession;
    onDelete?: () => void;
    onAccept?: () => void;
}

const defaultAppointmentCardOptions: AppointmentCardOptions = {
    //
};

enum OPTION_BUTTON_VALUE {
    CANCEL = "CANCEL"
}

@CustomElement({
    name: "appointment-card",
    style: require("./appointmentCard.scss"),
    template: require("./appointmentCard.html"),
    i18n: require("./appointmentCard.i18n").I18nAppointmentCard(),
    elementCssClasses: [W_100]
})
export class AppointmentCard<ElementTypes> extends WebComponent<_I18nAppointmentCard> {

    @Injected(BOOKING_SERVICE)
    private _bookingService!: AbstractClientBookingService;

    @Injected(ACTION_SERVICE)
    private _actionService!: ClientActionService<ElementTypes, unknown>;

    @TypedWebComponentOptions(defaultAppointmentCardOptions)
    public readonly options!: WebComponentOptions<AppointmentCardOptions>;

    @TypedHtmlElements({ ensureLoadedClasses: [CjsButton, CjsDropdownButton] })
    public readonly components!: {
        picture: HTMLDivElement,
        badge: HTMLDivElement,
        header: HTMLDivElement,
        footer: HTMLDivElement,
        date: HTMLSpanElement,
        time: HTMLSpanElement,
        call: CjsButton,
        accept: CjsButton,
        message: CjsButton,
        options: CjsDropdownButton<OPTION_BUTTON_VALUE>
    };

    constructor(options?: AppointmentCardOptions) {
        super();
        this.options.setWithoutRefresh(options);

        this.components.accept.options.setAndRefreshIfChanged({
            icon: { name: "fa-check", fixedWidth: true },
            iconMode: "only",
            cssClasses: ["accept"],
            size: ButtonSize.SMALL
        });
        this.components.accept.clickEvent.on(_ => {
            const userSession = this.options.userSession;
            this._bookingService.setCurrentSessionUser(userSession as anyReallyAny);
            if (userSession == null) { return; }
            this._bookingService.actionService.encapsulateInAction(action => {
                if (this._bookingService.isCurrentUserProfessional()) {
                    const session = this._bookingService.getById<BookingSession>("bookingSessions", userSession.sessionId);
                    action.updateValueIfNeeded(this._bookingService.tables.bookingSessions, session, "accepted", NOW());
                } else {
                    action.updateValueIfNeeded(this._bookingService.tables.bookingUserSessions, userSession, "accepted", NOW());
                }
            }, { logMessage: "Accept booking" }).then(_ => {
                this.refresh();
                this.options.onAccept?.();
            }).catch(CatchErrorWithDialog);

        });

        this.components.call.options.setAndRefreshIfChanged({
            icon: { name: "fa-phone", fixedWidth: true },
            iconMode: "only",
            size: ButtonSize.SMALL
        });
        this.components.message.options.setAndRefreshIfChanged({
            icon: { name: "fa-message", fixedWidth: true },
            iconMode: "only",
            size: ButtonSize.SMALL
        });
        this.components.options.options.setAndRefreshIfChanged({
            icon: { name: "fa-ellipsis-vertical", fixedWidth: true },
            iconMode: "only",
            size: ButtonSize.SMALL,
            arrow: false,
            dropDirection: "bottom",
            openMenuInBody: true,
            dropDownItemConfigs: [
                {
                    label: this.i18n.cancel,
                    icon: { name: "fa-times", fixedWidth: true },
                    value: OPTION_BUTTON_VALUE.CANCEL
                }
            ]
        });
        this.components.options.itemClickEvent.on(this._optionsButtonCallback.bind(this));
    }

    /** */
    private _optionsButtonCallback(e?: CustomEvent<DropdownButtonItemClickEvent<string>>): void {
        const userSession = this.options.userSession;
        if (userSession == null) { return; }
        const session = this._bookingService.getById<BookingSession>("bookingSessions", userSession.sessionId);

        switch (e?.detail.value) {
            case OPTION_BUTTON_VALUE.CANCEL: {
                if (session == null) { return; }
                this._bookingService.deleteUserSession(userSession.id).then(_ => {
                    this.options.onDelete?.();
                }).catch(CatchErrorWithDialog);

                break;
            }
            default: break;
        }
    }

    /** */
    public override refresh(): void {
        if (this.options.userSession == null) { return; }
        const session = this._bookingService.getById<BookingSession>("bookingSessions", this.options.userSession.sessionId);

        toggleVisible(this.components.accept, this._bookingService.isCurrentUserProfessional() ? session.accepted == null : this.options.userSession.accepted == null);

        const start = toDate(session.start);
        const finish = add(session.start, { second: session.duration });
        const userToDisplay = this._bookingService.isCurrentUserProfessional() ? this._bookingService.getUserById(this.options.userSession.customerId) : this._bookingService.getUserById(session.professionalId);

        this.components.badge.innerText = formatInitials(userToDisplay);
        this.components.header.innerText = `${userToDisplay.givenName} ${userToDisplay.familyName}`;
        this.components.date.innerText = format(start, "dd LLLL yyyy");
        this.components.time.innerText = `${format(start, "HH:mm")} → ${format(finish, "HH:mm")}`;
        if (start.day !== finish.day) {
            this.components.time.innerText += ` (le ${format(finish, "dd LLLL yyyy")})`;
        }

    }
}